import { useDisabledPageRedirect } from '../../lib/useDisabledPageRedirect';
import { Link } from '../../components/Link';
import { useAuth } from '../../lib/auth';
import { Icon } from '../../components/icons';
import { PublicFooter } from '../../components/PublicFooter';
import { PublicHeader } from '../../components/PublicHeader';
import { Button } from '../../components/ui';
import { Seo, breadcrumbFor } from '../../lib/seo';
import { useFeatures } from '../../lib/useFeatures';

/**
 * Marketing landing for the agency tier. Pitches the "one login, every client
 * workspace" story and the volume-pricing relationship, then routes prospects
 * into /register?agency=1.
 *
 * Pricing is intentionally not advertised on this page: discount ladders are
 * negotiated 1-on-1 with each agency based on roster size and commitment, so
 * there's nothing to publish here. The signup path is "register → talk to us
 * → ladder is set → portal unlocks" and the page copy reflects that.
 */
const VALUE_PROPS = [
  {
    icon: <Icon.Layers />,
    title: 'One login. Every client workspace.',
    body: 'The workspace switcher in the sidebar jumps you into any client tenant in one click. Fully isolated data per client — no cross-contamination of records, API keys, lists, or audit trail.',
  },
  {
    icon: <Icon.User />,
    title: 'Spin up clients in seconds',
    body: 'Add a new client workspace from the agency console with a name and an optional slug. The new workspace is live immediately with its own API key, its own quotas, and its own billing line.',
  },
  {
    icon: <Icon.ShieldCheck />,
    title: 'Volume pricing, negotiated with you',
    body: 'Agency discounts are tier-laddered — the more active client workspaces you run, the higher the discount on every one of their subscriptions. Tiers and breakpoints are agreed 1-on-1 during onboarding, not published. The agency portal unlocks once your ladder is in place.',
  },
  {
    icon: <Icon.Activity />,
    title: 'Cross-workspace dashboard',
    body: 'See every client at a glance — active count, applied discount, next discount cliff, per-workspace status. Pause, archive, or restore a client without touching Stripe.',
  },
];

const STEPS: Array<[string, string]> = [
  ['Register your agency', 'Sign up below — your workspace is automatically created in agency mode.'],
  ['We agree on a tier ladder', "We'll reach out for a short call to understand your roster and agree a discount ladder. Your agency console unlocks the moment your ladder is in place."],
  ['Discount grows as you grow', 'Every active client = +1 toward the next ladder breakpoint. Stripe is reconciled automatically — no manual reconciliation or support tickets.'],
];

export function AgenciesPage() {
  const { feature, isLoading: featuresLoading } = useFeatures();
  // Hide the whole agency landing page when the super-admin has globally disabled agency mode.
  // Redirect AFTER mount, not during render: platform flags are now seeded into the SSG snapshot
  // (to stop content reflow — see prefetch-public-data.mjs), so a render-time <Navigate> would
  // bake a redirect stub into the static HTML and break hydration. Also fixes a latent hooks
  // violation — the old early return sat above useAuth below, so hook order changed with the flag.
  useDisabledPageRedirect(!featuresLoading && !feature('agency'));
  const user = useAuth((s) => s.user);
  // Render nothing once the LIVE flag says agency mode is off, so an in-app navigation doesn't
  // flash the page before the redirect fires (same guard as the About page). Prerender-safe:
  // the flag defaults ENABLED at build, so this is false there and the full page still prerenders.
  if (!featuresLoading && !feature('agency')) return null;

  // Auth-aware CTAs: a signed-in agency owner already on a /app workspace gets a
  // direct jump to /app/agency; a standalone tenant gets the in-app "promote me"
  // path; a logged-out prospect goes through /register?agency=1.
  const ctaDestination = user
    ? user.tenant?.isAgency
      ? '/app/agency'
      : '/app/billing/subscription'
    : '/register?agency=1';
  const ctaLabel = user
    ? user.tenant?.isAgency
      ? 'Open agency console'
      : 'Promote your workspace'
    : 'Start your agency';

  return (
    <>
      <Seo
        title="Email integrity for agencies"
        description="Manage every client workspace from one login. Volume pricing on every client subscription, negotiated 1-on-1 — a tiered ladder that grows with your roster."
        path="/agencies"
        jsonLd={[breadcrumbFor([{ name: 'Home', path: '/' }, { name: 'Agencies', path: '/agencies' }])]}
      />
    <div className="mx-auto max-w-[1210px] px-6 pb-20">
      <PublicHeader />

      <section className="mx-auto max-w-3xl py-20 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-accentdeep/30 bg-accentdeep/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-accent">
          <span className="h-1.5 w-1.5 rounded-full bg-accent" /> For agencies & resellers
        </span>
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-[1.05] tracking-[-0.02em] md:text-6xl">
          One login. <span className="text-accent">Every client.</span>{' '}
          <span className="whitespace-nowrap">Volume pricing.</span>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-muted">
          Manage email integrity for every client workspace from a single agency console.
          Discount tiers are negotiated 1-on-1 during onboarding and grow with your roster —
          the deeper your client list, the cheaper every subscription.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link to={ctaDestination}>
            <Button variant="primary" className="h-12 px-6">
              {ctaLabel} <Icon.Play width={15} height={15} />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" className="h-12 px-6">
              See plan pricing
            </Button>
          </Link>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        {VALUE_PROPS.map((f) => (
          <div key={f.title} className="rounded-2xl border border-bordersoft bg-panel p-6">
            <span className="text-accent">{f.icon}</span>
            <h2 className="mt-3 text-base font-semibold">{f.title}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{f.body}</p>
          </div>
        ))}
      </section>

      <section className="mt-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-allow/30 bg-allow/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-allow">
            <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Volume pricing
          </span>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight">
            Negotiated pricing that grows with your roster.
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
            We don't publish a one-size-fits-all ladder. Every agency partner gets a tier
            schedule agreed during onboarding — based on your roster size, commitment, and
            how aggressively you plan to grow. The discount attaches automatically as a
            Stripe coupon to every active client subscription, and bumps up as you cross
            each breakpoint we agreed on together.
          </p>
        </div>
        <div className="mx-auto mt-10 grid max-w-3xl gap-3 md:grid-cols-3">
          {[
            ['Register', "Your agency workspace is created in agency mode with the portal in a 'pending review' state."],
            ['Talk to us', "Short call to understand your roster, current providers, and growth plans. We agree a tier ladder together."],
            ['Portal unlocks', "Your ladder is saved. From that moment, every new client subscription auto-receives the discount that matches your active-client count."],
          ].map(([title, body], i) => (
            <div key={title} className="rounded-2xl border border-bordersoft bg-panel p-5">
              <div className="font-mono text-[11px] text-accent">0{i + 1}</div>
              <h3 className="mt-1.5 text-sm font-semibold">{title}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-muted">{body}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-6 max-w-2xl text-center text-[12.5px] text-faint">
          The discount attaches as a Stripe coupon to every active client's subscription —
          never to paused or archived workspaces.
        </p>
      </section>

      <section className="mt-20">
        <h2 className="text-center font-display text-3xl font-bold tracking-tight">How it works</h2>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {STEPS.map(([title, body], i) => (
            <div key={title} className="rounded-2xl border border-bordersoft bg-panel p-6">
              <div className="font-mono text-sm text-accent">0{i + 1}</div>
              <h3 className="mt-2 text-base font-semibold">{title}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-20 rounded-2xl border border-bordersoft bg-panel p-10">
        <div className="grid items-start gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
              The same platform — just multiplied.
            </h2>
            <p className="mt-4 max-w-md text-[14px] leading-relaxed text-muted">
              Every client workspace runs the full SieveGuard stack: clustering, risk
              scoring, mailbox verification on Pro plans, plus every add-on (Domain Health,
              Inbox Placement, Phone Validation, Blacklist Checks). Nothing is gated by
              your agency tier — only the price.
            </p>
          </div>
          <ul className="grid gap-3 text-[13.5px]">
            {[
              'Per-workspace API keys, lists, webhooks, and audit log.',
              'Per-workspace billing — each client pays their own subscription with the agency discount auto-applied.',
              'Pause, archive, and restore client workspaces without touching Stripe.',
              'Cross-workspace dashboard with discount sync status — green = every active client carrying the right coupon.',
              'Tenant isolation enforced at the database layer (row-level security + tenant filter).',
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-allow" aria-hidden />
                <span className="text-muted">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-20 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-10 text-center">
        <h2 className="font-display text-3xl font-bold tracking-tight">
          Roll up email integrity across every client today.
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted">
          Create your agency workspace and add your first client in under five minutes. The
          volume discount kicks in automatically the moment you hit the first ladder
          threshold.
        </p>
        <Link to={ctaDestination} className="mt-6 inline-block">
          <Button variant="primary" className="h-12 px-6">
            {ctaLabel}
          </Button>
        </Link>
      </section>

      <PublicFooter />
    </div>
    </>
  );
}

// Default export required by React Router framework-mode route modules.
export default AgenciesPage;
